import { useEffect, useRef } from 'react';

import {
    Box,
    Button,
    Container,
    Dialog,
    DialogContent,
    TextField,
    Typography,
} from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { MetaTags } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import { PasswordInput } from 'src/components/FormikComponents';
import { useAuthContext } from 'src/contexts/AuthProvider';

import { NPILogo } from '../../assets/images';
import HelloSvg from '../../assets/images/HelloSVG';

import ForgotPassword from './ForgotPassword';

const LoginFormValidation = Yup.object().shape({
    username: Yup.string().max(255).required('Username is required'),
    password: Yup.string().required('Password is required'),
});

interface LoginFormData {
    username: string;
    password: string;
}

// TODO: remove hardcoded black text color
const LoginPage = () => {
    const { login } = useAuthContext();

    const [open, setOpen] = React.useState(false);
    const closeModal = () => setOpen(false);

    const usernameInputRef = useRef(null);

    const onLogin = async (
        values: LoginFormData,
        { setSubmitting }: FormikHelpers<LoginFormData>
    ) => {
        try {
            if (login) {
                await login(values.username, values.password);
            }
        } catch (error) {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            usernameInputRef.current.focus();
        }, 100);

        return () => {
            clearTimeout(timeout);
        };
    }, []);

    return (
        <>
            <MetaTags title="Login" description="Login page" />

            <Container
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    width: '100vw',
                    justifyContent: 'center',
                }}
            >
                <Box
                    width="100%"
                    display="flex"
                    padding="50px"
                    borderRadius="20px"
                    boxShadow="0 0 25px rgba(56, 11, 11, 0.29)"
                    sx={{ backgroundColor: 'white' }}
                >
                    <Box display="flex" flex={0.5}>
                        <HelloSvg />
                    </Box>
                    <Box marginLeft="80px" display="flex" flexDirection="column" flex={0.5}>
                        <NPILogo />
                        <Formik
                            initialValues={{
                                username: '',
                                password: '',
                            }}
                            validationSchema={LoginFormValidation}
                            onSubmit={onLogin}
                        >
                            {({ dirty, isValid, isSubmitting, errors }) => {
                                const disableButton = !dirty || !isValid || isSubmitting;
                                return (
                                    <Form>
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            gap="32px"
                                            maxWidth="400px"
                                            mt="20px"
                                        >
                                            <Box sx={{ w: '100%' }}>
                                                <Typography color="black" variant="h4" mb="16px">
                                                    LOG IN
                                                </Typography>
                                                <Typography color="#4D4D4D" variant="body2">
                                                    Welcome back! We&apos;re happy to see you again.
                                                    Please enter your login details below to access
                                                    your account
                                                </Typography>
                                            </Box>

                                            <Field dataTestId="username-input" name="username">
                                                {
                                                    // eslint-disable-next-line
                                                    ({ field, form }: any) => (
                                                        <TextField
                                                            error={
                                                                errors.username &&
                                                                form.touched.username
                                                            }
                                                            placeholder="Username"
                                                            {...field}
                                                            inputRef={usernameInputRef}
                                                            inputProps={{
                                                                'data-testid': `login-username-input`,
                                                            }}
                                                            id="outlined-error-helper-text"
                                                            helperText={
                                                                form.touched.username &&
                                                                errors?.username
                                                            }
                                                        />
                                                    )
                                                }
                                            </Field>

                                            <Flex flexDirection="column">
                                                <PasswordInput placeholder="Password" />

                                                <Typography
                                                    onClick={() => setOpen(true)}
                                                    alignSelf="flex-end"
                                                    marginTop="8px"
                                                    sx={{ cursor: 'pointer' }}
                                                    data-testid={'login-forgot-password'}
                                                >
                                                    Forgot Password
                                                </Typography>
                                            </Flex>

                                            <Button
                                                color="primary"
                                                fullWidth
                                                data-testid="login-submit-button"
                                                size="large"
                                                type="submit"
                                                variant="contained"
                                                disabled={disableButton}
                                                sx={{
                                                    bgcolor: '#000',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        bgcolor: '#000',
                                                        color: '#fff',
                                                        opacity: 0.8,
                                                    },
                                                }}
                                            >
                                                {isSubmitting ? 'Logging in...' : 'Next'}
                                            </Button>
                                        </Box>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </Box>
                </Box>
                <Dialog
                    open={open}
                    onClose={closeModal}
                    aria-labelledby="forget-password-dialog-title"
                    sx={{
                        '& .MuiDialog-paper': {
                            width: '400px',
                        },
                    }}
                >
                    <DialogContent>
                        <ForgotPassword handleClose={closeModal} />
                    </DialogContent>
                </Dialog>
            </Container>
        </>
    );
};

export default LoginPage;
